/**
 * File Explorer API wrapper.
 *
 * Thin typed shims over `/api/fs/*`. The Python service emits snake_case
 * JSON; the schemas in `@chaos/types/fs` mirror that verbatim. Consumers
 * read the raw field names directly so we don't need a separate camel-
 * case translation layer.
 */
import type {
  FsReadResponse,
  FsScopesResponse,
  FsSearchResponse,
  FsTreeNode,
} from '@chaos/types/fs';
import { env } from '../../env';
import { apiJson, apiUploadWithProgress } from './client';

interface ListOptions {
  path: string;
}

interface ReadOptions {
  path: string;
  offset?: number;
  limit?: number;
}

interface SearchOptions {
  q: string;
  root?: string;
  limit?: number;
}

interface UploadOptions {
  folder: string;
  file: File;
  overwrite?: boolean;
  signal?: AbortSignal;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  return search.toString();
}

export const fsApi = {
  scopes(): Promise<FsScopesResponse> {
    return apiJson<FsScopesResponse>('/fs/scopes');
  },

  list(opts: ListOptions): Promise<FsTreeNode> {
    return apiJson<FsTreeNode>(`/fs/list?${buildQuery({ path: opts.path })}`);
  },

  stat(opts: { path: string }): Promise<FsTreeNode> {
    return apiJson<FsTreeNode>(`/fs/stat?${buildQuery({ path: opts.path })}`);
  },

  read(opts: ReadOptions): Promise<FsReadResponse> {
    return apiJson<FsReadResponse>(
      `/fs/read?${buildQuery({
        path: opts.path,
        offset: opts.offset,
        limit: opts.limit,
      })}`,
    );
  },

  /**
   * Build the absolute download URL so `<img>`, `<video>`, `<embed>`,
   * etc. can fetch the binary directly (cookies are sent because the
   * page origin matches).
   */
  downloadUrl(path: string): string {
    return `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/fs/download?${buildQuery({ path })}`;
  },

  /**
   * URL for a formatted standalone view. For Markdown the gateway renders safe
   * HTML; for other types it transparently redirects to the raw download. Used
   * by the viewer's "open in new tab" so a .md opens nicely formatted.
   */
  viewUrl(path: string): string {
    return `${env.VITE_API_BASE_URL.replace(/\/$/, '')}/fs/view?${buildQuery({ path })}`;
  },

  search(opts: SearchOptions): Promise<FsSearchResponse> {
    return apiJson<FsSearchResponse>(
      `/fs/search?${buildQuery({
        q: opts.q,
        root: opts.root,
        limit: opts.limit,
      })}`,
    );
  },

  /**
   * Multipart upload with byte progress, retry/backoff on a transient
   * failure, and a stall timeout. A file explorer upload is replaceable
   * (the file is still on the user's disk), so retry — not a browser-side
   * copy of the bytes — is the right safety net here.
   *
   * THE ONE ROUTE WHERE THE PRE-SEND READ COSTS SOMETHING (2026-08-16). Every
   * uploader now reads and counts its body before sending it, which means one
   * payload held in memory; here the ceiling is FS_UPLOAD_MAX_MB, 256 MB by
   * default, the largest of any route. That is the price of the guarantee, and
   * it is paid knowingly: the gateway already buffers roughly the same amount
   * to parse the multipart, and the alternative — proving the bytes are
   * readable and then handing the original File to the transport anyway — puts
   * the send back on an unobserved second read, which is the defect itself.
   * If this ceiling is ever raised meaningfully, the answer is the resumable
   * chunk ingress in lib/voice-upload.ts, not dropping the check.
   *
   * A zero-byte upload is allowed through deliberately: an empty file is a
   * real thing to drag into an explorer, so `readBodyVerified` enforces "the
   * body length equals the source length", not "the body is non-empty".
   * 409 (file exists → the overwrite prompt) was already never auto-retried.
   */
  upload(opts: UploadOptions): Promise<FsTreeNode> {
    return apiUploadWithProgress<FsTreeNode>('/fs/upload', opts.file, {
      filename: opts.file.name,
      fields: { folder: opts.folder, overwrite: opts.overwrite ? 'true' : 'false' },
      ...(opts.signal ? { signal: opts.signal } : {}),
      ...(opts.onProgress
        ? {
            onProgress: (p: { loaded: number; total: number }): void =>
              opts.onProgress!({ loaded: p.loaded, total: p.total }),
          }
        : {}),
    });
  },
};

export const fsKeys = {
  all: ['fs'] as const,
  scopes: () => [...fsKeys.all, 'scopes'] as const,
  list: (path: string) => [...fsKeys.all, 'list', path] as const,
  read: (path: string, offset: number, limit: number) =>
    [...fsKeys.all, 'read', path, offset, limit] as const,
  search: (q: string, root: string | undefined) => [...fsKeys.all, 'search', q, root] as const,
};
